/**
 * Australian date display (feedback item 4, 2026-07-19).
 *
 * Clients see and type dates the Australian way - DD/MM/YYYY for full dates,
 * MM/YYYY for month-and-year dates - while everything under the hood stays
 * ISO (YYYY-MM-DD / YYYY-MM / YYYY): storage, validation, cross-field
 * comparisons, the gap engine and the PDF all keep reading ISO exactly as
 * before. The conversion lives at the input edge only (DateTextField).
 *
 * Parsing is forgiving: ISO input is still accepted (older saved values,
 * pasted values, the browser-automation harness), single-digit days and
 * months are fine, and dots or hyphens work as separators.
 */

/** ISO 'YYYY-MM-DD' -> 'DD/MM/YYYY'; anything else passes through untouched. */
export function isoToDisplayDate(v: string | null | undefined): string {
  if (!v) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  return m ? `${m[3]}/${m[2]}/${m[1]}` : v;
}

/** ISO 'YYYY-MM' -> 'MM/YYYY'; bare 'YYYY' and anything else pass through. */
export function isoToDisplayPartial(v: string | null | undefined): string {
  if (!v) return '';
  const t = v.trim();
  const ym = /^(\d{4})-(\d{2})$/.exec(t);
  if (ym) return `${ym[2]}/${ym[1]}`;
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (ymd) return `${ymd[2]}/${ymd[1]}`; // legacy full date in a partial field
  return t; // bare year or free text
}

const pad = (n: string) => n.padStart(2, '0');

/**
 * 'DD/MM/YYYY' (also D/M/YYYY, dots or hyphens) or ISO -> ISO 'YYYY-MM-DD'.
 * Returns null when the text is not recognisably a date, so the caller can
 * hand the raw text to validation instead of guessing.
 */
export function displayToIsoDate(text: string): string | null {
  const t = text.trim();
  if (t === '') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t; // already ISO
  const dmy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(t);
  if (dmy) return `${dmy[3]}-${pad(dmy[2])}-${pad(dmy[1])}`;
  return null;
}

/**
 * 'MM/YYYY' (also M/YYYY, dots or hyphens), bare 'YYYY', ISO 'YYYY-MM', or a
 * full date in either style -> ISO partial ('YYYY-MM' or 'YYYY').
 */
export function displayToIsoPartial(text: string): string | null {
  const t = text.trim();
  if (t === '') return '';
  if (/^\d{4}(-\d{2}){0,2}$/.test(t)) {
    return t.length > 7 ? t.slice(0, 7) : t; // ISO; truncate a full date to the month
  }
  const my = /^(\d{1,2})[/.-](\d{4})$/.exec(t);
  if (my) return `${my[2]}-${pad(my[1])}`;
  const dmy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(t);
  if (dmy) return `${dmy[3]}-${pad(dmy[2])}`; // full date typed: keep month + year
  return null;
}
